import { useEffect, useRef, useState } from "react";
import { LinearProgress } from "@mui/material";
import { styled, IventisTheme } from "@iventis/styles";

/**
 * Configuration for the progress tracking hook
 */
export interface ProgressConfig<T> {
    /** The current status */
    status: T;
    /** Function that returns true when progress should be active */
    isLoading: (status: T) => boolean;
    /** Function that returns true when progress should be complete (100%) */
    isComplete: (status: T) => boolean;
    /** Duration in milliseconds for the progress animation */
    durationMs: number;
}

/**
 * Hook for managing progress state with a timer
 */
export const useProgressTimer = <T>({ status, isLoading, isComplete, durationMs }: ProgressConfig<T>) => {
    const [progress, setProgress] = useState<number>(0);
    const progressTimerRef = useRef<number | null>(null);

    // Cleanup on unmount
    useEffect(
        () => () => {
            if (progressTimerRef.current != null) {
                clearInterval(progressTimerRef.current);
                progressTimerRef.current = null;
            }
        },
        []
    );

    useEffect(() => {
        if (isLoading(status)) {
            if (progressTimerRef.current != null) return; // already running
            setProgress(0);
            const start = Date.now();
            progressTimerRef.current = window.setInterval(() => {
                const elapsed = Date.now() - start;
                const pct = Math.min(100, (elapsed / durationMs) * 100);
                setProgress(pct);
                if (elapsed >= durationMs && progressTimerRef.current != null) {
                    clearInterval(progressTimerRef.current);
                    progressTimerRef.current = null;
                }
            }, 200);
            return;
        }

        // on any other status clear timer & set progress
        if (progressTimerRef.current != null) {
            clearInterval(progressTimerRef.current);
            progressTimerRef.current = null;
        }
        setProgress(isComplete(status) ? 100 : 0);
    }, [status]);

    return progress;
};

/**
 * Shared pulsating loading component
 */
export const PulsatingLoading = styled(LinearProgress)`
    width: 30%;
    /* make the bar use the theme's primary typography colour */
    .MuiLinearProgress-bar {
        background-color: ${({ theme }: { theme: IventisTheme }) => theme.typographyColors.core} !important;
    }
    animation: pulsate 2s infinite;
    @keyframes pulsate {
        0% {
            opacity: 0.5;
        }
        50% {
            opacity: 1;
        }
        100% {
            opacity: 0.5;
        }
    }
`;
